/**
 * SessionGeometry for an MPR reslice pane (axial volume rendered at a
 * coronal/sagittal orientation).
 *
 * The rendered slices belong to the viewport's reoriented plane, which
 * cornerstone owns — so navigation is INDEX-based (NavigationController →
 * showIndex → GpuVolumeStrategy.jumpToSlice, which steps along the current
 * orientation) and the slice count comes from the LIVE viewport
 * (`getNumberOfSlices()`), NOT the axial instance list. `ippFor` is therefore
 * never consulted (navigatesByWorld=false), and `anatomicSign` is a fixed
 * wheel-up→+index mapping (refined against on-screen behavior).
 *
 * Pure except the injected `getViewport` thunk (read lazily so the count is
 * correct once the volume actor is attached). No React, no atoms.
 */
import type { SessionGeometry } from "./ViewportSession";
import type { PlaneOrientation, WorldPos } from "./types";

/** The slice of the cornerstone viewport surface this geometry depends on. */
export interface ResliceViewportLike {
  getNumberOfSlices?: () => number;
}

export function createResliceGeometry(
  plane: PlaneOrientation,
  getViewport: () => ResliceViewportLike | null | undefined,
): SessionGeometry {
  return {
    // Index-based nav (navigatesByWorld=false) never calls this.
    ippFor(): WorldPos | null {
      return null;
    },
    anatomicSign(): 1 | -1 {
      return 1;
    },
    totalSlices(): number {
      const n = getViewport()?.getNumberOfSlices?.();
      return typeof n === "number" && n > 0 ? n : 1;
    },
    orientation(): PlaneOrientation {
      return plane;
    },
    navigatesByWorld(): boolean {
      return false;
    },
  };
}
